import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class PerformanceLoggerService {
  private performanceMarks = new Map<string, number>();

  constructor(
    private logger: LoggerService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  startTiming(label: string): void {
    this.performanceMarks.set(label, Date.now());

    if (isPlatformBrowser(this.platformId) && 'performance' in window) {
      performance.mark(`${label}-start`);
    }
  }

  endTiming(label: string, context?: any): number {
    const startTime = this.performanceMarks.get(label);
    if (!startTime) {
      this.logger.warn(`Performance timing '${label}' was not started`, context, 'PerformanceLogger');
      return 0;
    }

    const duration = Date.now() - startTime;
    this.performanceMarks.delete(label);

    if (isPlatformBrowser(this.platformId) && 'performance' in window) {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
    }

    this.logger.info(`Performance: ${label}`, {
      duration,
      timestamp: new Date().toISOString(),
      ...context
    }, 'PerformanceLogger');

    return duration;
  }

  logPageLoad(): void {
    if (!isPlatformBrowser(this.platformId) || !('performance' in window)) {
      return;
    }

    // Wait for page to fully load
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

        if (navigation) {
          this.logger.info('Page Load Performance', {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            totalPageLoad: navigation.loadEventEnd - navigation.fetchStart,
            domInteractive: navigation.domInteractive - navigation.fetchStart,
            firstPaint: this.getFirstPaint(),
            firstContentfulPaint: this.getFirstContentfulPaint()
          }, 'PerformanceLogger');
        }
      }, 0);
    });
  }

  private getFirstPaint(): number | null {
    if (!('performance' in window)) return null;

    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? firstPaint.startTime : null;
  }

  private getFirstContentfulPaint(): number | null {
    if (!('performance' in window)) return null;

    const paintEntries = performance.getEntriesByType('paint');
    const firstContentfulPaint = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return firstContentfulPaint ? firstContentfulPaint.startTime : null;
  }

  logMemoryUsage(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const memory = (performance as any).memory;
    if (memory) {
      this.logger.info('Memory Usage', {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        memoryUsagePercentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
      }, 'PerformanceLogger');
    }
  }
}
